import { useCallback, useMemo } from 'react'

import { clsx } from 'clsx'

import { useRouter } from '@tribeplatform/react-sdk'
import { useActivityLogs } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'
import { List } from '@tribeplatform/react-ui-kit/Layout'

import { T } from '../i18n/components/T.js'
import { UpgradeBanner } from '../UpgradeBanner/UpgradeBanner.js'

import { parseLog, AuditLogRow } from './AuditLogRow.js'
import { SampleAuditLogs } from './placeholder.js'

const ROWS_PER_PAGE = 30

export interface AuditLogListProps {
  authorized: boolean
  filters: {
    spaceId?: string
    actorId?: string
    postId?: string
    memberId?: string
    noun?: string
    from?: number
  }
  page?: number
  onPageChange: (page: number) => void
}

export const AuditLogList = ({
  authorized,
  page,
  filters,
  onPageChange,
}: AuditLogListProps) => {
  const { spaceId, actorId, postId, memberId, noun, from } = filters
  const filterBy = useMemo(
    () => ({
      spaceId,
      actorId,
      postId,
      memberId,
      noun,
      verb: undefined,
    }),
    [actorId, memberId, noun, postId, spaceId],
  )

  const { Link } = useRouter()
  const billingUrl = '/manage/billing/plans'

  const { data: activityLogs, isInitialLoading } = useActivityLogs({
    variables: {
      query: generateAuditLogQuery({
        filterBy,
        from,
        limit: ROWS_PER_PAGE,
        offset: (page - 1) * ROWS_PER_PAGE,
      }),
    },
    fields: {
      entities: {
        actor: 'basic',
        member: 'basic',
        app: 'basic',
        post: {
          space: 'basic',
        },
        space: 'basic',
        tag: 'basic',
      },
    },
    useQueryOptions: {
      refetchOnMount: 'always',
      enabled: authorized,
    },
  })

  const handlePageIncrement = useCallback(() => {
    const nextPage = page + 1
    onPageChange(nextPage)
  }, [page, onPageChange])

  const handlePageDecrement = useCallback(() => {
    const prevPage = page > 2 ? page - 1 : 1
    onPageChange(prevPage)
  }, [page, onPageChange])

  let content
  if (isInitialLoading) {
    content = <AuditLogListLoading count={ROWS_PER_PAGE} />
  } else if (!authorized) {
    if (postId) {
      content = <SampleAuditLogs type="post" />
    } else if (actorId) {
      content = <SampleAuditLogs type="member" />
    } else if (spaceId) {
      content = <SampleAuditLogs type="space" />
    } else {
      content = <SampleAuditLogs type="network" />
    }
  } else if (activityLogs?.length) {
    content = (
      <div className="relative">
        <List spacing="md">
          {activityLogs?.map(log => {
            const parsedLog = parseLog(log)
            return <AuditLogRow key={parsedLog?.payload?.id} log={parsedLog} />
          })}
        </List>
        <div className="absolute -z-10 w-[2px] ms-[1px] top-8 start-4 bottom-8 bg-surface-pressed" />
      </div>
    )
  } else {
    content = (
      <div className="py-5 text-center">
        <T
          id="AuditLog.AuditLogList.NoAuditLogAvailable"
          defaultMessage="No audit logs available in the selected timeframe."
          description="The text showing there’s no audit log available in the selected time frame"
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col space-y-5">
      {!authorized ? (
        <Link href={billingUrl}>
          <UpgradeBanner>
            <T
              id="AuditLog.AuditLogList.AuditLogRequiredPlan"
              defaultMessage="Audit logs is not available on your plan"
              description="Text showing your plan does not support audit logs"
            />
          </UpgradeBanner>
        </Link>
      ) : null}
      {content}
      {authorized && (
        <div className="flex space-s-3 items-center text-center mt-3">
          <Button
            fullWidth
            size="lg"
            variant="secondaryNeutral"
            className="flex-1"
            disabled={page <= 1}
            onClick={handlePageDecrement}
            leadingIcon={
              <SvgIcon className="rtl:-scale-x-100" name="arrow-left" />
            }
          >
            <T
              id="AuditLog.AuditLogList.PreviousButton"
              defaultMessage="Previous"
              description="Previous button text"
            />
          </Button>
          <div className="flex-1">
            <T
              id="AuditLog.AuditLogList.PageNumber"
              defaultMessage="Page {number}"
              description="Page number (eg: Page 2)"
              values={{ number: page }}
            />
          </div>
          <Button
            fullWidth
            size="lg"
            variant="secondaryNeutral"
            className="flex-1"
            loading={isInitialLoading}
            onClick={handlePageIncrement}
            disabled={activityLogs?.length < ROWS_PER_PAGE}
            trailingIcon={
              <SvgIcon className="rtl:-scale-x-100" name="arrow-right" />
            }
          >
            <T
              id="AuditLog.AuditLogList.NextButton"
              defaultMessage="Next"
              description="Next button text"
            />
          </Button>
        </div>
      )}
    </div>
  )
}

const AuditLogListLoading = ({ count = 10 }) => {
  return (
    <div className="relative animate-pulse">
      <List spacing="md">
        {[...Array(count).keys()].map((_e, idx) => (
          <List.Item key={_e} className="flex items-center space-s-3 h-[58px]">
            <div className="rounded-button bg-skeleton h-9 w-9" />
            <div
              className={clsx(
                'h-5 bg-skeleton rounded-full flex',
                idx % 2 === 0 ? 'w-1/3' : 'w-1/4',
              )}
            />
          </List.Item>
        ))}
      </List>
      <div className="absolute -z-10 w-[2px] ms-[1px] top-8 start-4 bottom-8 bg-skeleton" />
    </div>
  )
}

const generateAuditLogQuery = ({
  filterBy,
  from,
  limit = 100,
  offset = null,
}) => {
  let where = ''

  const ignoredNouns = [
    'TRACKER',
    'EXTRA_PROPERTY',
    'MEMBER_BADGE',
    'MEMBER_SESSION',
  ]
  Object.keys(filterBy).forEach(key => {
    if (filterBy[key]) {
      where += `and ${key}='${filterBy[key]}' `
    }
  })
  return `select id, createdAt, noun, verb, appId, actorId, memberId, spaceId, postId, object ${
    from ? `timeframe from ${from} ` : ''
  }where ${ignoredNouns
    .map(noun => `noun!='${noun}'`)
    .join(' and ')} ${where}order by createdAt desc limit ${limit}${
    offset ? ` offset ${offset}` : ''
  }`
}
